import axios from 'axios';
import { McpChatRequest, McpChatResponse, McpStreamEvent } from '../../Types/McpChat/McpChat';

const mcpAxiosInstance = axios.create({
  baseURL: process.env.REACT_APP_MCP_ENDPOINT,
});

export type McpWarmupResponse = {
  warmed: boolean;
  agentCached: boolean;
  conversationId?: string;
};

/**
 * Pre-connect MCP and optionally cache an Analyse agent for a conversation
 * so the first prompt skips cold setup. Soft-fails on the server.
 */
export async function warmupMcpAgent(
  token: string,
  clientId: string,
  conversationId?: string,
  signal?: AbortSignal
): Promise<McpWarmupResponse> {
  const body = {
    clientId,
    mode: 'Analyse' as const,
    ...(conversationId
      ? { conversationId, contextId: conversationId }
      : {}),
  };

  const response = await mcpAxiosInstance.post<McpWarmupResponse>('/api/chat/warmup', body, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
    signal,
  });

  return response.data;
}

export async function cancelMcpChat(
  token: string,
  conversationId: string
): Promise<{ cancelled: boolean }> {
  const response = await mcpAxiosInstance.post<{ cancelled: boolean }>(
    '/api/chat/cancel',
    { conversationId },
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response.data;
}

/**
 * Sends a message to the MCP agent and consumes the Server-Sent Events stream,
 * invoking onEvent for every progress/tool/delta event as the agent works.
 * Resolves with the full answer text once the stream completes.
 *
 * Uses fetch (not EventSource) because the endpoint requires a POST body
 * and an Authorization header, neither of which EventSource supports.
 */
export async function sendMcpMessageStreaming(
  token: string,
  message: string,
  clientId: string,
  onEvent: (event: McpStreamEvent) => void,
  conversationId?: string,
  contextId?: string,
  signal?: AbortSignal,
  extras?: Pick<McpChatRequest, 'instructions' | 'mode' | 'analyseContext' | 'allowedFileIds'>
): Promise<McpChatResponse> {
  const body: McpChatRequest = {
    message,
    clientId,
    ...(conversationId ? { conversationId } : {}),
    ...(contextId ? { contextId } : {}),
    ...(extras?.instructions ? { instructions: extras.instructions } : {}),
    ...(extras?.mode ? { mode: extras.mode } : {}),
    ...(extras?.analyseContext ? { analyseContext: extras.analyseContext } : {}),
    ...(extras?.allowedFileIds?.length ? { allowedFileIds: extras.allowedFileIds } : {}),
  };

  const IDLE_MS = 45_000;
  const watchdog = new AbortController();
  const onCallerAbort = () => watchdog.abort();
  if (signal) {
    if (signal.aborted) {
      watchdog.abort();
    } else {
      signal.addEventListener('abort', onCallerAbort, { once: true });
    }
  }

  let idleTimer: ReturnType<typeof setTimeout> | null = null;
  const clearIdle = () => {
    if (idleTimer !== null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };
  const resetIdle = () => {
    clearIdle();
    idleTimer = setTimeout(() => watchdog.abort(), IDLE_MS);
  };
  resetIdle();

  try {
    const response = await fetch(`${process.env.REACT_APP_MCP_ENDPOINT}/api/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      signal: watchdog.signal,
    });

    if (!response.ok || !response.body) {
      const error = new Error(`Stream request failed with status ${response.status}`);
      (error as Error & { status?: number }).status = response.status;
      throw error;
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let answer = '';
    let resolvedConversationId = conversationId ?? '';
    let streamError: string | null = null;

    const handleEvent = (event: McpStreamEvent) => {
      resetIdle();
      if (event.type === 'delta') {
        answer += event.text ?? '';
      } else if (event.type === 'done' && event.conversationId) {
        resolvedConversationId = event.conversationId;
      } else if (event.type === 'error') {
        streamError = event.text ?? 'Something went wrong.';
      }
      onEvent(event);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      resetIdle();

      buffer += decoder.decode(value, { stream: true });

      // SSE frames are separated by a blank line; keep any trailing partial frame.
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        const dataLine = frame
          .split('\n')
          .find((line) => line.startsWith('data: '));
        if (!dataLine) continue;

        try {
          handleEvent(JSON.parse(dataLine.slice(6)) as McpStreamEvent);
        } catch {
          // Ignore malformed frames rather than killing the whole stream.
        }
      }
    }

    if (streamError) {
      throw new Error(streamError);
    }

    return { answer, conversationId: resolvedConversationId };
  } catch (error) {
    if (signal?.aborted) {
      throw error;
    }
    if (watchdog.signal.aborted) {
      throw new Error('Mia took too long to respond. Please try again.');
    }
    throw error;
  } finally {
    clearIdle();
    signal?.removeEventListener('abort', onCallerAbort);
  }
}
